import { FC, useEffect, useRef } from 'react';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { displayToastError } from '@/helpers';
import { NavigateToRootEvent } from '@/router/state/root/component/root-event';
import { useRootActorRef } from '@/router/state/root/util/context/helpers';
import { logger } from '@/utils/logger/logger';

import { clearOAuthState, getOAuthState } from '../utils/oauth-state';

const TAG = 'OAuthCallbackPage:';

/**
 * OAuth callback page that receives OAuth parameters and redirects to the original page
 * with the parameters as URL params. The actual OAuth processing is handled by
 * the OAuthProcessor component on the destination page.
 */
export const OAuthCallbackPage: FC = () => {
  const { t } = useTranslation(['authenticated.settings']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const actorRef = useRootActorRef();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Ensure this effect only runs once, even if component re-renders during navigation
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    logger.trace(`search params are: `, {
      searchParams: Object.fromEntries(searchParams.entries()),
    });
    const state = searchParams.get('state');
    const code = searchParams.get('code');
    const realmId = searchParams.get('realmId');

    if (!state || !code) {
      logger.error(TAG, 'Missing required OAuth parameters', { state, code });
      displayToastError(t('pages.integrations.oauth.missingParameters'));
      clearOAuthState();
      actorRef.send(new NavigateToRootEvent());
      navigate('/');
      return;
    }

    // Verify OAuth state exists
    const oauthState = getOAuthState();
    if (!oauthState) {
      logger.error(TAG, 'OAuth state not found or expired');
      displayToastError(t('pages.integrations.oauth.stateNotFound'));
      actorRef.send(new NavigateToRootEvent());
      navigate('/');
      return;
    }

    logger.debug(TAG, 'Redirecting to return URL with OAuth params', {
      integration: oauthState.integration,
      returnUrl: oauthState.returnUrl,
      hasRealmId: !!realmId,
    });

    // Build the return URL with OAuth parameters
    const returnUrl = new URL(oauthState.returnUrl, window.location.origin);
    returnUrl.searchParams.set('integration', oauthState.integration);
    returnUrl.searchParams.set('state', state);
    returnUrl.searchParams.set('code', code);
    if (realmId) {
      returnUrl.searchParams.set('realmId', realmId);
    }

    // Navigate to the return URL with OAuth parameters
    navigate(returnUrl.pathname + returnUrl.search, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-full max-w-md space-y-6 px-4">
        <div className="text-center">
          <Loader2 className="mx-auto h-4 w-4 animate-spin text-primary" />
        </div>
      </div>
    </div>
  );
};
